import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  UsersIcon,
  AcademicCapIcon,
  CalendarIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  TrophyIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, Badge, Avatar, InlineSpinner } from '@/components/ui'
import apiClient from '@/api/client'

interface DashboardStats {
  users: {
    total: number
    pending_approval: number
    new_this_month: number
    active_this_week: number
  }
  courses: {
    total: number
    published: number
    enrollments: number
    completions_this_month: number
  }
  events: {
    upcoming: number
    registrations_this_month: number
  }
  forum: {
    threads_this_month: number
    posts_this_month: number
  }
  documents: {
    total: number
    downloads_this_month: number
  }
  taskforces: {
    active: number
    open_tickets: number
  }
}

interface RecentActivity {
  id: number
  type: 'user_registered' | 'course_completed' | 'event_registered' | 'forum_post' | 'document_uploaded'
  description: string
  user: {
    id: number
    full_name: string
    avatar: string | null
  }
  created_at: string
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    setLoading(true)
    try {
      // Fetch stats - we'll use placeholder data structure
      // In production, this would be a real API call
      const [statsResponse, activityResponse] = await Promise.all([
        apiClient.get('/admin/dashboard/stats/').catch(() => ({ data: null })),
        apiClient.get('/admin/dashboard/activity/').catch(() => ({ data: [] })),
      ])

      // Use mock data if API not available, merging with defaults
      const defaultStats = {
        users: { total: 0, pending_approval: 0, new_this_month: 0, active_this_week: 0 },
        courses: { total: 0, published: 0, enrollments: 0, completions_this_month: 0 },
        events: { upcoming: 0, registrations_this_month: 0 },
        forum: { threads_this_month: 0, posts_this_month: 0 },
        documents: { total: 0, downloads_this_month: 0 },
        taskforces: { active: 0, open_tickets: 0 },
      }
      const responseData = statsResponse.data || {}
      setStats({
        users: { ...defaultStats.users, ...responseData.users },
        courses: { ...defaultStats.courses, ...responseData.courses },
        events: { ...defaultStats.events, ...responseData.events },
        forum: { ...defaultStats.forum, ...responseData.forum },
        documents: { ...defaultStats.documents, ...responseData.documents },
        taskforces: { ...defaultStats.taskforces, ...responseData.taskforces },
      })
      setRecentActivity(activityResponse.data || [])
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getActivityIcon = (type: RecentActivity['type']) => {
    switch (type) {
      case 'user_registered':
        return <UsersIcon className="h-5 w-5 text-blue-500" />
      case 'course_completed':
        return <AcademicCapIcon className="h-5 w-5 text-green-500" />
      case 'event_registered':
        return <CalendarIcon className="h-5 w-5 text-purple-500" />
      case 'forum_post':
        return <ChatBubbleLeftRightIcon className="h-5 w-5 text-orange-500" />
      case 'document_uploaded':
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />
      default:
        return <CheckCircleIcon className="h-5 w-5 text-gray-400" />
    }
  }

  if (loading) {
    return <InlineSpinner message="Caricamento dashboard..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Amministrazione</h1>
        <p className="text-gray-600">Panoramica della piattaforma community</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to="/gestione/users" className="group">
          <Card className="hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                <UsersIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Gestione Utenti</p>
                {stats?.users.pending_approval ? (
                  <Badge variant="warning" size="sm">{stats.users.pending_approval} in attesa</Badge>
                ) : (
                  <p className="text-xs text-gray-500">{stats?.users.total} utenti</p>
                )}
              </div>
            </div>
          </Card>
        </Link>

        <Link to="/gestione/courses" className="group">
          <Card className="hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                <AcademicCapIcon className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Corsi</p>
                <p className="text-xs text-gray-500">{stats?.courses.published} pubblicati</p>
              </div>
            </div>
          </Card>
        </Link>

        <Link to="/gestione/events" className="group">
          <Card className="hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                <CalendarIcon className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Eventi</p>
                <p className="text-xs text-gray-500">{stats?.events.upcoming} in programma</p>
              </div>
            </div>
          </Card>
        </Link>

        <Link to="/gestione/documents" className="group">
          <Card className="hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                <DocumentTextIcon className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Documenti</p>
                <p className="text-xs text-gray-500">{stats?.documents.total} documenti</p>
              </div>
            </div>
          </Card>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Users Stats */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <UsersIcon className="h-6 w-6 text-blue-600" />
            </div>
            {stats?.users.new_this_month && stats.users.new_this_month > 0 ? (
              <div className="flex items-center text-green-600 text-sm">
                <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                +{stats.users.new_this_month}
              </div>
            ) : null}
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.users.total || 0}</p>
          <p className="text-sm text-gray-500">Utenti totali</p>
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Attivi questa settimana</span>
              <span className="font-medium text-gray-900">{stats?.users.active_this_week || 0}</span>
            </div>
            {stats?.users.pending_approval ? (
              <div className="flex justify-between text-sm">
                <span className="text-orange-500">In attesa approvazione</span>
                <span className="font-medium text-orange-600">{stats.users.pending_approval}</span>
              </div>
            ) : null}
          </div>
        </Card>

        {/* Courses Stats */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <AcademicCapIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.courses.enrollments || 0}</p>
          <p className="text-sm text-gray-500">Iscrizioni corsi</p>
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Corsi pubblicati</span>
              <span className="font-medium text-gray-900">{stats?.courses.published || 0}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Completamenti mese</span>
              <span className="font-medium text-green-600">{stats?.courses.completions_this_month || 0}</span>
            </div>
          </div>
        </Card>

        {/* Forum Stats */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-orange-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.forum.posts_this_month || 0}</p>
          <p className="text-sm text-gray-500">Post questo mese</p>
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Thread questo mese</span>
              <span className="font-medium text-gray-900">{stats?.forum.threads_this_month || 0}</span>
            </div>
          </div>
        </Card>

        {/* Task Force Stats */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <UserGroupIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.taskforces.active || 0}</p>
          <p className="text-sm text-gray-500">Task Force attive</p>
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Ticket aperti</span>
              <span className="font-medium text-orange-600">{stats?.taskforces.open_tickets || 0}</span>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card>
          <CardHeader title="Attività recente" />
          {recentActivity.length > 0 ? (
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{activity.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Avatar size="xs" name={activity.user.full_name} src={activity.user.avatar || undefined} />
                      <span className="text-xs text-gray-500">{activity.user.full_name}</span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-400">{formatDate(activity.created_at)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">Nessuna attività recente</p>
          )}
        </Card>

        {/* Pending Actions */}
        <Card>
          <CardHeader title="Azioni in sospeso" />
          <div className="space-y-4">
            {stats?.users.pending_approval ? (
              <Link to="/gestione/users?status=pending" className="flex items-center justify-between p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                <div className="flex items-center gap-3">
                  <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
                  <span className="text-sm text-gray-900">Utenti in attesa di approvazione</span>
                </div>
                <Badge variant="warning">{stats.users.pending_approval}</Badge>
              </Link>
            ) : null}

            {stats?.taskforces.open_tickets ? (
              <Link to="/taskforce" className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <div className="flex items-center gap-3">
                  <ClockIcon className="h-5 w-5 text-blue-500" />
                  <span className="text-sm text-gray-900">Ticket aperti nelle Task Force</span>
                </div>
                <Badge variant="info">{stats.taskforces.open_tickets}</Badge>
              </Link>
            ) : null}

            {stats?.events.upcoming ? (
              <Link to="/events" className="flex items-center justify-between p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                <div className="flex items-center gap-3">
                  <CalendarIcon className="h-5 w-5 text-purple-500" />
                  <span className="text-sm text-gray-900">Eventi in programma</span>
                </div>
                <Badge variant="default">{stats.events.upcoming}</Badge>
              </Link>
            ) : null}

            {!stats?.users.pending_approval && !stats?.taskforces.open_tickets && (
              <div className="text-center py-8">
                <CheckCircleIcon className="h-12 w-12 text-green-300 mx-auto mb-2" />
                <p className="text-gray-500">Tutto in ordine!</p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Admin Quick Links */}
      <Card>
        <CardHeader title="Gestione rapida" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Link to="/gestione/users" className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-colors">
            <UsersIcon className="h-8 w-8 text-gray-400 mb-2" />
            <span className="text-sm text-gray-700">Utenti</span>
          </Link>
          <Link to="/gestione/courses" className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-colors">
            <AcademicCapIcon className="h-8 w-8 text-gray-400 mb-2" />
            <span className="text-sm text-gray-700">Corsi</span>
          </Link>
          <Link to="/gestione/events" className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-colors">
            <CalendarIcon className="h-8 w-8 text-gray-400 mb-2" />
            <span className="text-sm text-gray-700">Eventi</span>
          </Link>
          <Link to="/gestione/documents" className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-colors">
            <DocumentTextIcon className="h-8 w-8 text-gray-400 mb-2" />
            <span className="text-sm text-gray-700">Documenti</span>
          </Link>
          <Link to="/gestione/forum" className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-colors">
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-gray-400 mb-2" />
            <span className="text-sm text-gray-700">Forum</span>
          </Link>
          <Link to="/gestione/gamification" className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-colors">
            <TrophyIcon className="h-8 w-8 text-gray-400 mb-2" />
            <span className="text-sm text-gray-700">Gamification</span>
          </Link>
        </div>
      </Card>
    </div>
  )
}
